import { Inject, Injectable } from '@angular/core';
import { IClientOptions } from '../../interfaces/embedding-options.interface';

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  private _isBlocked: boolean = true;
  private _trustedParent: boolean = false;

  get isBlocked(): boolean {
    return this._isBlocked;
  }

  constructor(@Inject('Window') private window: Window) { }

  /**
   * Check whether the parent page that sent the postMessage is
   * on the same origin as this app. The browser guarantees the
   * `event.origin` value, so this cannot be spoofed by a
   * third-party embedder.
   */
  public checkTrustedParentOrigin(parentOrigin: string | null): void {
    if (parentOrigin && parentOrigin === this.window.location.origin) {
      this._trustedParent = true;
    }
  }

  /**
   * Authorize a popped-out top-level window. Called by the chat component only
   * after PopoutService confirms a valid same-origin handshake from the opener
   * iframe — the iframe is the trust anchor that already passed the
   * `requireEmbedding`/`requiredReferrers` checks, so the popout inherits that
   * grant. Never called from the `?popout=1` URL param alone.
   */
  public authorizePopout(): void {
    this._trustedParent = true;
    this._isBlocked = false;
  }


  checkEmbeddingAccess(options: IClientOptions): boolean {
    if (this._trustedParent) {
      this._isBlocked = false;
      return true;
    }
    if (options.requireEmbedding) {
      // Check if the page is embedded
      if (this.window.self === this.window.top) {
        this._isBlocked = true;
        return false;
      }
    }

    const requiredUrls = options.requiredReferrers?.split('\n').map((url) => url.trim()).filter((url) => url && url.length > 0);

    if (options.requiredReferrers && requiredUrls && requiredUrls.length > 0) {
      let parentHref: string | null = null;
      try {
        parentHref = (this.window.self !== this.window.top) ? this.window.parent.document.location.href : null;
      } catch {}
      const parentReferrer = document.referrer;
      if (!parentReferrer && !parentHref) {
        this._isBlocked = true;
        return false;
      }

      const parentReferrerMatch = requiredUrls.some((requiredReferrer) => {
        // improvement options:
        // https://www.npmjs.com/package/browser-extension-url-match
        return parentReferrer.startsWith(requiredReferrer);
      });
      if (!parentReferrerMatch) {
        // try parentHref
        if (parentHref) {
          const parentHrefMatch = requiredUrls.some((requiredReferrer) => {
            return parentHref!.startsWith(requiredReferrer);
          });
          if (!parentHrefMatch) {
            this._isBlocked = true;
            return false;
          }
        } else {
          this._isBlocked = true;
          return false;
        }
      }
    }

    this._isBlocked = false;
    return true;
  }
}
